import { SettingsType } from '../../../../shared/api/pipeex/pcfSettingsClient';
import type { SettingInfoDto } from '../../../../shared/api/pipeex/pcfSettingsClient';
import { DEFAULT_PIPEEX_PROFILE_NAME } from '../../../../shared/config/defaultPipeexProfile/defaultPipeexProfile';

export interface ProfileIdentity {
  name: string;
  settingsType: SettingsType;
}

export const parseSettingsType = (raw: unknown): SettingsType => {
  if (typeof raw === 'number') {
    return raw === SettingsType.Company ? SettingsType.Company : SettingsType.User;
  }

  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized === '0' || normalized === 'company') {
      return SettingsType.Company;
    }
    if (normalized === '1' || normalized === 'user') {
      return SettingsType.User;
    }
  }

  return SettingsType.User;
};

export const buildProfileKey = (
  first: ProfileIdentity | string,
  second?: SettingsType
): string => {
  if (typeof first === 'string') {
    return `${first}::${second}`;
  }

  return `${first.name}::${first.settingsType}`;
};

export const deduplicateProfilesByKey = (
  profiles: ProfileIdentity[]
): ProfileIdentity[] => {
  const seen = new Set<string>();

  return profiles.filter((profile) => {
    const key = buildProfileKey(profile);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

/**
 * Resolves a unique name for a transient wizard flow snapshot profile.
 * If `baseName` is not already used by a profile in the list, it is returned.
 * Otherwise returns `baseName 2`, `baseName 3`, ... for the first unused suffix.
 * Name matching is case-sensitive but defensive against malformed entries.
 */
export const resolveUniqueFlowSnapshotProfileName = (
  profiles: SettingInfoDto[],
  baseName = DEFAULT_PIPEEX_PROFILE_NAME
): string => {
  const profileNames = new Set(
    profiles
      .filter((profile): profile is SettingInfoDto & { name: string } => typeof profile?.name === 'string')
      .map((profile) => profile.name)
  );

  if (!profileNames.has(baseName)) {
    return baseName;
  }

  let suffix = 2;
  while (profileNames.has(`${baseName} ${suffix}`)) {
    suffix += 1;
  }

  return `${baseName} ${suffix}`;
};

/**
 * Returns true when `name` is the base flow snapshot profile name or one of its
 * incremental variants (e.g. "Default" or "Default 2", "Default 3", ...).
 */
export const isFlowSnapshotDefaultProfileName = (
  name: string,
  baseName = DEFAULT_PIPEEX_PROFILE_NAME
): boolean => {
  if (typeof name !== 'string') {
    return false;
  }

  if (name === baseName) {
    return true;
  }

  const escapedBaseName = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escapedBaseName} \\d+$`).test(name);
};
